import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import {
  WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_REAL_TIME,
  WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_RULE_SETS,
} from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { IntegrityMonitorRuleProcessingModeOption } from '@nirvana/shared/dtos/types/policies/common';
import {
  getIntegrityMonitorRealTimeSearchableKeywords,
  INTEGRITY_MONITOR_REAL_TIME_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/integrityMonitor/integrity-monitor.values';
import {
  IntegrityMonitorRealTimeFormValue,
  IntegrityMonitorRealTimeSearchableKeywords,
} from '../../../../utils/uiSettings/integrityMonitor/integrity-monitor.types';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzIntegrityMonitorRuleSetsWebComponent } from '../../ruleSetsSection';
import { areArraysEqual } from '@nirvana/shared/basic-types/object';
import { combineLatest, skip } from 'rxjs';
import { DataLoadedStatus, PolicyAppState } from '../../../../redux/types';
import {
  getIsIntegrityMonitorCompanyRuleSetsLoaded,
  getIsIntegrityMonitorSelectedRuleSetsLoaded,
} from '../../../../redux/customStates/integrityMonitor/integrity-monitor.actions';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_REAL_TIME)
export class GzIntegrityMonitorRealTimeWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  IntegrityMonitorRealTimeFormValue,
  IntegrityMonitorRealTimeSearchableKeywords
> {
  /**
   * Path for Integrity Monitor module.
   */
  protected path = {
    moduleId: ModuleId.INTEGRITY_MONITOR,
    sectionId: SectionId.INTEGRITY_MONITOR_REAL_TIME,
  };

  /**
   * Searchable keywords for Integrity Monitor section.
   */
  protected searchableKeywords = getIntegrityMonitorRealTimeSearchableKeywords();

  @query('#integrity-monitor-real-time-toggle')
  private toggleFormField: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;

  @query('#integrity-monitor-real-time-info-text')
  private infoText: GzTextWebComponent;

  @query('#integrity-monitor-real-time-content')
  private content: HTMLElement;

  @query('#integrity-monitor-real-time-rule-processing-mode')
  private ruleProcessingModeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('#integrity-monitor-real-time-mode')
  private modeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query(WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_RULE_SETS)
  private ruleSetsSection: GzIntegrityMonitorRuleSetsWebComponent;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="integrity-monitor-real-time-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[IntegrityMonitorRealTimeSearchableKeywords.IntegrityMonitor].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
        ><span
          >${this.searchableKeywords[IntegrityMonitorRealTimeSearchableKeywords.IntegrityMonitor].translatedLabel}</span
        ></gz-toggle
      >
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="integrity-monitor-real-time-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Enables real-time monitoring of events generated for files, folders, registry entries, users, services, and installed software on target endpoints.'
      )}</gz-text
    >`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="integrity-monitor-real-time-content">
        <gz-title
          searchable-label="${this.searchableKeywords[
            IntegrityMonitorRealTimeSearchableKeywords.IntegrityProcessingMode
          ].label}"
          class="module-subtitle"
          id="integrity-monitor-real-time-rule-processing-mode-title"
        >
          ${this.searchableKeywords[IntegrityMonitorRealTimeSearchableKeywords.IntegrityProcessingMode].translatedLabel}
        </gz-title>

        <gz-labeled-content
          id="integrity-monitor-real-time-rule-sets-section-labeled-content"
          hasFormLabelWidth
          labelFontSize="13"
          labelFontWeight="400"
          label="${translateFn('Select mode:')}"
        >
          <gz-form-field id="integrity-monitor-real-time-rule-processing-mode" required>
            <gz-radio-group gz-form-input>
              <gz-radio
                id="im-realtime-rule-processing-mode-fast"
                label="${translateFn('Fast')}"
                .value="${IntegrityMonitorRuleProcessingModeOption.FAST}"
              ></gz-radio>
              <gz-radio
                id="im-realtime-rule-processing-mode-normal"
                label="${translateFn('Normal')}"
                .value="${IntegrityMonitorRuleProcessingModeOption.NORMAL}"
              ></gz-radio>
              <gz-radio
                id="im-realtime-rule-processing-mode-slow"
                label="${translateFn('Slow')}"
                .value="${IntegrityMonitorRuleProcessingModeOption.SLOW}"
              ></gz-radio>
            </gz-radio-group>
            <gz-input-error></gz-input-error>
          </gz-form-field>
        </gz-labeled-content>

        <gz-title
          searchable-label="${this.searchableKeywords[IntegrityMonitorRealTimeSearchableKeywords.IntegrityRealTime]
            .label}"
          class="module-subtitle"
          id="im-realtime-mode-title"
        >
          ${this.searchableKeywords[IntegrityMonitorRealTimeSearchableKeywords.IntegrityRealTime].translatedLabel}
        </gz-title>
        <gz-form-field id="integrity-monitor-real-time-mode" required>
          <gz-radio-group gz-form-input>
            <gz-radio id="integrity-monitor-real-time-mode-baseline"></gz-radio>
            <gz-radio id="integrity-monitor-real-time-mode-rule-sets"></gz-radio>
          </gz-radio-group>
          <gz-input-error></gz-input-error>
        </gz-form-field>

        <gz-policy-panel-integrity-monitor-rule-sets></gz-policy-panel-integrity-monitor-rule-sets>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.integrityMonitor.realTime
   */
  protected getValueFromManager(): IntegrityMonitorRealTimeFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.INTEGRITY_MONITOR]: {
          [SectionId.INTEGRITY_MONITOR_REAL_TIME]: INTEGRITY_MONITOR_REAL_TIME_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.INTEGRITY_MONITOR]: {
          [SectionId.INTEGRITY_MONITOR_REAL_TIME]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.integrityMonitor.realTime'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Get additional information from manager and set missing fields.
   * Fields used in this section:
   * - integrityMonitor
   */
  protected getAdditionalInfoFromManager() {
    // get default value
    const defaultAdditionalInfo = {
      integrityMonitor: {
        companyRuleSets: [],
      },
    };

    // get value from manager
    const additionalInfo = {
      integrityMonitor: PolicyPanelManager.getInstance().getAdditionalPolicyInfo(['integrityMonitor']),
    };

    // merge received value with default one
    return deepmerge(defaultAdditionalInfo, additionalInfo);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        integrityMonitor: {
          realTime: {
            enabled: this.toggleFormField,
            ruleProcessingMode: this.ruleProcessingModeFormField,
            mode: this.modeFormField,
          },
        },
      },
    });
  }

  /**
   * Subscribe to validity event received from form group and rule sets section.
   */
  protected subscribeToValidityEvent() {
    // combine into a single observable (skip first value, because it is always true)
    const formGroupValidSubscription = combineLatest([this.formGroup.valid$, this.ruleSetsSection.valid$])
      .pipe(skip(1))
      .subscribe({
        next: ([formGroupValid, ruleSetsSectionValid]) => {
          this.changeValidityIfNecessary(formGroupValid && ruleSetsSectionValid);
        },
      });

    this.subscriptions.push(formGroupValidSubscription);
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        integrityMonitor: {
          realTime: {
            enabled: this.valueT.uiSettings.integrityMonitor.realTime.enabled,
            ruleProcessingMode: this.valueT.uiSettings.integrityMonitor.realTime.ruleProcessingMode,
            mode: this.valueT.uiSettings.integrityMonitor.realTime.mode,
          },
        },
      },
    };
    this.ruleSetsSection.value = [...this.valueT.uiSettings.integrityMonitor.realTime.ruleSetIds];

    // set additional info for rule sets section
    if (this.additionalInfo.integrityMonitor.selectedRuleSets) {
      this.ruleSetsSection.additionalInfo = this.additionalInfo.integrityMonitor;
      return;
    }
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    _.set(value, ['uiSettings', 'integrityMonitor', 'realTime', 'ruleSetIds'], this.ruleSetsSection.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'integrityMonitor', 'realTime'], {});
    }

    return value;
  }

  /**
   * Send additional information to manager if needed.
   */
  protected sendAdditionalInfoToManagerIfNeeded() {
    if (!this.additionalInfo.integrityMonitor.selectedRuleSets) {
      return;
    }

    PolicyPanelManager.getInstance().setAdditionalPolicyInfoToPath(
      ['integrityMonitor', 'selectedRuleSets'],
      this.ruleSetsSection.selectedRuleSets
    );
  }

  /**
   * Get info about unsaved changes.
   */
  protected hasUnsavedChanges() {
    if (!this.valueT) {
      return false;
    }

    // verify if there are unsaved changes in rule sets
    const areRuleSetsEqual = areArraysEqual(
      this.valueT.uiSettings.integrityMonitor.realTime.ruleSetIds,
      this.ruleSetsSection.value
    );
    return this.formGroup.hasUnsavedChanges || !areRuleSetsEqual;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.infoText, !event.detail.checked);
    toggleDisabled(this.content, !event.detail.checked);
    this.ruleSetsSection.validationDisabled = !event.detail.checked;
  }

  /**
   * Override reset method, because we need to reset also rule sets section.
   */
  public reset() {
    super.reset();
    this.ruleSetsSection.reset();
  }

  /**
   * Override method to check if additional info is loaded.
   * @param policiesApp
   */
  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return (
      getIsIntegrityMonitorCompanyRuleSetsLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED &&
      getIsIntegrityMonitorSelectedRuleSetsLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_INTEGRITY_MONITOR_REAL_TIME: GzIntegrityMonitorRealTimeWebComponent;
  }
}
